import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  Optional,
  PLATFORM_ID,
  Renderer2,
  Self,
  ViewContainerRef
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FindReplacePanelComponent } from '../components/find-replace-panel/find-replace-panel.component';
import { FindReplaceScopeDirective } from './find-replace-scope.directive';
import {
  buildHighlightHtml,
  buildReplaceAllPatch,
  FindMatch,
  findMatches,
  matchIndexAt
} from '../util/find-replace.util';

/** Space reserved on the right of the textarea so note text never runs under the icon. */
const TRIGGER_GUTTER = 30;

/** How many of this field's replaces stay undoable. */
const UNDO_DEPTH = 20;

/** One replace, kept so it can be rolled back. */
interface UndoEntry {
  before: string;
  after: string;
}

/**
 * Copied onto the mirror so its line breaks land in exactly the same places as
 * the textarea's. Anything that changes where text wraps has to be in here.
 */
export const MIRRORED_STYLE_PROPS = [
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'font-variant',
  'letter-spacing',
  'word-spacing',
  'line-height',
  'text-transform',
  'text-indent',
  'text-align',
  'direction',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'tab-size'
];

/**
 * Adds a find & replace control to any textarea.
 *
 *   <textarea matInput appFindReplace formControlName="notes"></textarea>
 *
 * A small icon sits in the top-right corner of the field; clicking it opens a
 * mat-menu with a find field and a replace field. Find is case-insensitive and
 * highlights every hit inside the note. Replace inserts the replacement text
 * verbatim — case-sensitive, whitespace intact — over the matched span only.
 *
 * Highlighting a native textarea isn't possible directly, so the directive
 * renders a mirror of the note behind it: same font, padding, width and scroll
 * offset, with <mark> elements where the matches are. The textarea stays a plain
 * textarea, which keeps the caret, spellcheck, autosize and form binding intact.
 */
@Directive({
  selector: 'textarea[appFindReplace]',
  exportAs: 'appFindReplace'
})
export class FindReplaceDirective implements AfterViewInit, OnDestroy {
  /** Pass `false` to leave the textarea untouched (e.g. a read-only summary). */
  @Input() set appFindReplace(value: '' | boolean | undefined | null) {
    this.enabled = value !== false;
  }

  @Input() findReplaceTooltip = 'Find & replace';

  /** Ctrl/Cmd+F opens the panel while the caret is in the textarea. */
  @Input() findReplaceHotkey = true;

  private enabled = true;
  private textarea!: HTMLTextAreaElement;
  private mount!: HTMLElement;
  private panelRef?: ComponentRef<FindReplacePanelComponent>;
  private backdrop?: HTMLElement;
  private mirror?: HTMLElement;
  private resizeObserver?: ResizeObserver;
  private readonly subs = new Subscription();

  private term = '';
  private matches: FindMatch[] = [];
  private activeIndex = -1;
  /** Per-field by construction: one directive instance per textarea. */
  private readonly undoStack: UndoEntry[] = [];
  /** Guards the re-entrant input/valueChanges storm our own edits set off. */
  private applyingEdit = false;
  /** Whether the most recent replace came from the scope rather than this field. */
  private lastEditWasScope = false;

  constructor(
    private host: ElementRef<HTMLTextAreaElement>,
    private renderer: Renderer2,
    private vcr: ViewContainerRef,
    private zone: NgZone,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Self() private ngControl: NgControl | null,
    @Optional() private scope: FindReplaceScopeDirective | null
  ) {}

  ngAfterViewInit(): void {
    if (!this.enabled || !isPlatformBrowser(this.platformId)) {
      return;
    }

    this.textarea = this.host.nativeElement;
    this.mount = this.textarea.parentElement ?? this.textarea;

    this.prepareHost();
    this.createPanel();

    this.zone.runOutsideAngular(() => {
      this.textarea.addEventListener('scroll', this.onScroll, { passive: true });
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.onGeometryChange());
        this.resizeObserver.observe(this.textarea);
      }
    });

    // The note can also be rewritten from outside — an AI draft landing in the
    // form control fires no DOM input event, so highlights would go stale.
    if (this.ngControl?.valueChanges) {
      this.subs.add(
        this.ngControl.valueChanges.subscribe(() => {
          if (this.applyingEdit || !this.term) {
            return;
          }
          setTimeout(() => this.recompute(false));
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.resizeObserver?.disconnect();
    if (this.textarea) {
      this.textarea.removeEventListener('scroll', this.onScroll);
    }
    if (this.backdrop?.parentNode) {
      this.renderer.removeChild(this.backdrop.parentNode, this.backdrop);
    }
    this.panelRef?.destroy();
  }

  @HostListener('input')
  onHostInput(): void {
    if (this.applyingEdit) {
      return;
    }
    // Typing over our own edit retires the undo entry, so this has to run even
    // when no search is active.
    this.syncUndoState();
    if (!this.term) {
      return;
    }
    this.recompute(false);
  }

  @HostListener('keydown', ['$event'])
  onHostKeydown(event: KeyboardEvent): void {
    if (!this.findReplaceHotkey || !this.panelRef) {
      return;
    }
    if ((event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'f') {
      event.preventDefault();
      event.stopPropagation();
      this.panelRef.instance.open(this.selectedSeed());
    }
  }

  // --- setup -------------------------------------------------------------

  private prepareHost(): void {
    if (getComputedStyle(this.mount).position === 'static') {
      this.renderer.setStyle(this.mount, 'position', 'relative');
    }

    const paddingRight = parseFloat(getComputedStyle(this.textarea).paddingRight) || 0;
    if (paddingRight < TRIGGER_GUTTER) {
      this.renderer.setStyle(this.textarea, 'padding-right', `${TRIGGER_GUTTER}px`);
    }

    this.renderer.addClass(this.textarea, 'app-fr-textarea');
  }

  private createPanel(): void {
    this.panelRef = this.vcr.createComponent(FindReplacePanelComponent);
    const panel = this.panelRef.instance;
    panel.tooltip = this.findReplaceTooltip;

    // Created in the directive's view container, then moved next to the
    // textarea so it can be positioned against the field's corner.
    this.renderer.appendChild(this.mount, this.panelRef.location.nativeElement);

    this.subs.add(panel.findChange.subscribe(term => this.onFindChange(term)));
    this.subs.add(panel.next.subscribe(() => this.step(1)));
    this.subs.add(panel.previous.subscribe(() => this.step(-1)));
    this.subs.add(panel.replaceOne.subscribe(() => this.replaceCurrent()));
    this.subs.add(panel.replaceAll.subscribe(() => this.replaceEvery()));
    this.subs.add(panel.replaceEverywhere.subscribe(() => void this.replaceAcrossScope()));
    this.subs.add(panel.undo.subscribe(() => this.undoLast()));
    this.subs.add(panel.opened.subscribe(() => this.onPanelOpened()));
    this.subs.add(panel.closed.subscribe(() => this.clearHighlights()));

    this.panelRef.changeDetectorRef.detectChanges();
    this.positionTrigger();
  }

  /**
   * Pins the icon to the textarea's own top-right rather than the form field's,
   * so it lands in the same place whatever appearance the field uses. clientWidth
   * stops at the scrollbar, which keeps the icon off the scroll thumb.
   */
  private positionTrigger(): void {
    const trigger = this.panelRef?.location.nativeElement as HTMLElement | undefined;
    if (!trigger || !this.textarea.clientWidth) {
      // Not laid out yet (collapsed panel, inactive tab). The stylesheet's
      // top/right fallback holds until the ResizeObserver reports a real box.
      return;
    }

    const styles = getComputedStyle(this.textarea);
    const borderTop = parseFloat(styles.borderTopWidth) || 0;
    const borderLeft = parseFloat(styles.borderLeftWidth) || 0;
    const width = trigger.offsetWidth || TRIGGER_GUTTER;

    trigger.style.right = 'auto';
    trigger.style.top = `${this.textarea.offsetTop + borderTop}px`;
    trigger.style.left = `${this.textarea.offsetLeft + borderLeft + this.textarea.clientWidth - width}px`;
  }

  private ensureBackdrop(): void {
    if (this.backdrop) {
      return;
    }
    this.backdrop = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(this.backdrop, 'app-fr-backdrop');
    this.renderer.setAttribute(this.backdrop, 'aria-hidden', 'true');

    this.mirror = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(this.mirror, 'app-fr-mirror');
    this.renderer.appendChild(this.backdrop, this.mirror);

    this.renderer.insertBefore(this.mount, this.backdrop, this.textarea);
  }

  // --- search ------------------------------------------------------------

  private onPanelOpened(): void {
    const panel = this.panelRef?.instance;
    if (!panel) {
      return;
    }
    panel.canReplace = !this.textarea.readOnly && !this.textarea.disabled;

    const seed = this.selectedSeed();
    if (seed) {
      panel.setFind(seed);
    }

    this.term = panel.find;
    this.syncUndoState();
    this.recompute(true);
  }

  private onFindChange(term: string): void {
    this.term = term ?? '';
    this.recompute(true);
  }

  /**
   * @param fromCaret start the active match at the caret rather than keeping the
   * current position — what you want on a new search, not on a re-render.
   */
  private recompute(fromCaret: boolean): void {
    const text = this.textarea.value ?? '';
    this.matches = findMatches(text, this.term);

    if (!this.matches.length) {
      this.activeIndex = -1;
    } else if (fromCaret || this.activeIndex < 0) {
      this.activeIndex = matchIndexAt(this.matches, this.textarea.selectionStart ?? 0);
    } else {
      this.activeIndex = Math.min(this.activeIndex, this.matches.length - 1);
    }

    this.panelRef?.instance.setMatchState(this.matches.length, this.activeIndex);
    this.syncScopeState();
    this.render();
  }

  /** Scope totals are recomputed from the controls, so they cover hidden fields too. */
  private syncScopeState(): void {
    this.panelRef?.instance.setScopeState(
      !!this.scope,
      this.scope && this.term ? this.scope.countMatches(this.term) : 0
    );
  }

  private step(delta: number): void {
    if (!this.matches.length) {
      return;
    }
    this.activeIndex = (this.activeIndex + delta + this.matches.length) % this.matches.length;
    this.panelRef?.instance.setMatchState(this.matches.length, this.activeIndex);
    this.render();
  }

  private render(): void {
    if (!this.term) {
      this.clearHighlights();
      return;
    }

    this.ensureBackdrop();
    const mirror = this.mirror;
    const backdrop = this.backdrop;
    if (!mirror || !backdrop) {
      return;
    }

    backdrop.style.display = '';
    this.positionTrigger();
    this.syncGeometry();
    // Safe: buildHighlightHtml escapes the note; the only markup is its own <mark>.
    mirror.innerHTML = buildHighlightHtml(this.textarea.value ?? '', this.matches, this.activeIndex);
    this.renderer.addClass(this.textarea, 'app-fr-searching');

    this.scrollActiveIntoView();
    this.syncScroll();
  }

  private clearHighlights(): void {
    if (this.mirror) {
      this.mirror.innerHTML = '';
    }
    if (this.backdrop) {
      this.backdrop.style.display = 'none';
    }
    this.renderer.removeClass(this.textarea, 'app-fr-searching');
  }

  // --- replace -----------------------------------------------------------

  private replaceCurrent(): void {
    if (this.activeIndex < 0 || !this.matches.length || !this.canEdit()) {
      return;
    }
    const match = this.matches[this.activeIndex];
    const replacement = this.panelRef?.instance.replace ?? '';
    const before = this.textarea.value ?? '';

    this.splice(match.start, match.end, replacement);
    this.pushUndo(before);
    this.settleAfterEdit(match.start + replacement.length);
  }

  private replaceEvery(): void {
    if (!this.matches.length || !this.canEdit()) {
      return;
    }
    const replacement = this.panelRef?.instance.replace ?? '';
    const patch = buildReplaceAllPatch(this.textarea.value ?? '', this.matches, replacement);
    if (!patch) {
      return;
    }

    const before = this.textarea.value ?? '';
    this.splice(patch.start, patch.end, patch.text);
    this.pushUndo(before);
    this.settleAfterEdit(patch.start);
  }

  /**
   * Hands off to the scope, which confirms with the clinician before touching
   * fields they may not have open. The edit lands on the form controls, so this
   * textarea re-highlights itself through the valueChanges subscription; we only
   * need to refresh the counts once the dialog is done.
   */
  private async replaceAcrossScope(): Promise<void> {
    if (!this.scope || !this.term || !this.canEdit()) {
      return;
    }
    const replacement = this.panelRef?.instance.replace ?? '';
    const summary = await this.scope.replaceEverywhere(this.term, replacement);
    if (!summary) {
      return;
    }

    this.lastEditWasScope = true;
    this.undoStack.length = 0;
    this.settleAfterEdit(this.textarea.selectionStart ?? 0);
    this.syncUndoState();
  }

  private pushUndo(before: string): void {
    this.lastEditWasScope = false;
    this.undoStack.push({ before, after: this.textarea.value ?? '' });
    if (this.undoStack.length > UNDO_DEPTH) {
      this.undoStack.shift();
    }
    this.syncUndoState();
  }

  /**
   * Undo is offered only while the note still reads exactly as this directive
   * left it. If the clinician has typed since, rolling back would silently throw
   * that away, so the button goes dark and the browser's own Ctrl+Z takes over.
   */
  private canUndo(): boolean {
    const last = this.undoStack[this.undoStack.length - 1];
    return !!last && last.after === (this.textarea.value ?? '') && this.canEdit();
  }

  private undoLast(): void {
    // A scope replace supersedes this field's own stack: it is the more recent
    // edit and spans fields this directive cannot see.
    if (this.lastEditWasScope) {
      if (this.scope?.canUndo()) {
        this.scope.undo();
        this.lastEditWasScope = false;
        this.settleAfterEdit(this.textarea.selectionStart ?? 0);
        this.syncUndoState();
      }
      return;
    }

    if (!this.canUndo()) {
      return;
    }
    const entry = this.undoStack.pop() as UndoEntry;
    this.splice(0, (this.textarea.value ?? '').length, entry.before);
    // The entry below is the one that produced this value, so undo keeps walking
    // back through the field's own replaces.
    this.syncUndoState();
    this.settleAfterEdit(0);
  }

  private syncUndoState(): void {
    const canUndo = this.lastEditWasScope ? !!this.scope?.canUndo() : this.canUndo();
    this.panelRef?.instance.setCanUndo(canUndo, this.lastEditWasScope);
  }

  private canEdit(): boolean {
    return !this.textarea.readOnly && !this.textarea.disabled;
  }

  /**
   * Swaps [start, end) for `insert`, leaving every other character of the note
   * exactly as it was. Uses execCommand first so the browser's own undo stack
   * survives — Ctrl+Z after a replace is what clinicians expect — and falls back
   * to writing the control directly where that isn't supported.
   */
  private splice(start: number, end: number, insert: string): void {
    const textarea = this.textarea;
    const previouslyFocused = this.doc.activeElement as HTMLElement | null;
    let applied = false;

    this.applyingEdit = true;
    try {
      if (insert) {
        try {
          textarea.focus({ preventScroll: true });
          textarea.setSelectionRange(start, end);
          applied = this.doc.execCommand('insertText', false, insert);
        } catch {
          applied = false;
        }
      }

      if (!applied) {
        const value = textarea.value ?? '';
        const next = value.slice(0, start) + insert + value.slice(end);
        textarea.value = next;
        textarea.setSelectionRange(start + insert.length, start + insert.length);
        // One notification covers every binding style: Angular's value accessor
        // marks the control dirty and pushes to formControlName/ngModel alike,
        // and cdkTextareaAutosize re-measures off the same event.
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
      }
    } finally {
      this.applyingEdit = false;
    }

    // Hand focus back to whichever menu field the user was in.
    if (previouslyFocused && previouslyFocused !== textarea) {
      previouslyFocused.focus({ preventScroll: true });
    }
  }

  private settleAfterEdit(caret: number): void {
    const text = this.textarea.value ?? '';
    this.matches = findMatches(text, this.term);
    this.activeIndex = matchIndexAt(this.matches, caret);
    this.panelRef?.instance.setMatchState(this.matches.length, this.activeIndex);
    this.render();
  }

  // --- geometry ----------------------------------------------------------

  private syncGeometry(): void {
    const textarea = this.textarea;
    const backdrop = this.backdrop;
    const mirror = this.mirror;
    if (!backdrop || !mirror || !textarea.clientWidth) {
      return;
    }

    const styles = getComputedStyle(textarea);
    const borderTop = parseFloat(styles.borderTopWidth) || 0;
    const borderLeft = parseFloat(styles.borderLeftWidth) || 0;

    // clientWidth/Height are the padding box: border excluded, and the scrollbar
    // gutter excluded too, so the mirror wraps where the textarea wraps.
    backdrop.style.top = `${textarea.offsetTop + borderTop}px`;
    backdrop.style.left = `${textarea.offsetLeft + borderLeft}px`;
    backdrop.style.width = `${textarea.clientWidth}px`;
    backdrop.style.height = `${textarea.clientHeight}px`;

    mirror.style.width = `${textarea.clientWidth}px`;
    MIRRORED_STYLE_PROPS.forEach(prop =>
      mirror.style.setProperty(prop, styles.getPropertyValue(prop))
    );
  }

  private readonly onScroll = (): void => this.syncScroll();

  private syncScroll(): void {
    if (!this.mirror) {
      return;
    }
    this.mirror.style.transform = `translate(${-this.textarea.scrollLeft}px, ${-this.textarea.scrollTop}px)`;
  }

  private onGeometryChange(): void {
    this.positionTrigger();
    if (!this.term || !this.backdrop) {
      return;
    }
    this.syncGeometry();
    this.syncScroll();
  }

  /**
   * The mirror is a pixel-accurate copy of the note, so the active <mark> tells
   * us exactly where in the textarea to scroll to.
   */
  private scrollActiveIntoView(): void {
    const mark = this.mirror?.querySelector('.app-fr-hit--active') as HTMLElement | null;
    if (!mark) {
      return;
    }

    const textarea = this.textarea;
    const gutter = 24;
    const top = mark.offsetTop;
    const bottom = top + mark.offsetHeight;

    if (top < textarea.scrollTop + gutter) {
      textarea.scrollTop = Math.max(0, top - gutter);
    } else if (bottom > textarea.scrollTop + textarea.clientHeight - gutter) {
      textarea.scrollTop = bottom - textarea.clientHeight + gutter;
    }
  }

  /** The current selection, when it looks like something worth searching for. */
  private selectedSeed(): string {
    const start = this.textarea.selectionStart ?? 0;
    const end = this.textarea.selectionEnd ?? 0;
    if (end <= start) {
      return '';
    }
    const selection = (this.textarea.value ?? '').slice(start, end);
    return selection.includes('\n') ? '' : selection;
  }
}
